import { useEffect, useState } from "react";
import api from "../api";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line
} from "recharts";

function Statistiques() {

  const [global, setGlobal] =
    useState(null);

  const [revenusMensuels, setRevenusMensuels] =
    useState([]);

  const [croissance, setCroissance] =
    useState([]);

  const [rentabilite, setRentabilite] =
    useState([]);

  const [topClients, setTopClients] =
    useState([]);

  const [clientsVIP, setClientsVIP] =
    useState([]);

  const [clientsRisque, setClientsRisque] =
    useState([]);

  const [alertes, setAlertes] =
    useState([]);

  const [showAllAlertes, setShowAllAlertes] =
    useState(false);

  const [periode, setPeriode] =
    useState("mois");

  useEffect(() => {
    fetchData();
  }, [periode]);

  // =====================
  // FETCH
  // =====================

  const fetchData = async () => {

    try {
      setGlobal(
        (
          await api.get(
            `/statistiques/global?periode=${periode}`
          )
        ).data
      );
    } catch {}

    try {
      setRevenusMensuels(
        (
          await api.get(
            `/statistiques/revenus-mensuels?periode=${periode}`
          )
        ).data
      );
    } catch {}

    try {
      setCroissance(
        (
          await api.get(
            `/statistiques/croissance-mensuelle`
          )
        ).data
      );
    } catch {}

    try {
      setRentabilite(
        (
          await api.get(
            `/statistiques/rentabilite?periode=${periode}`
          )
        ).data
      );
    } catch {}

    try {
      setTopClients(
        (
          await api.get(
            `/statistiques/top-clients?periode=${periode}`
          )
        ).data
      );
    } catch {}

    try {
      setClientsVIP(
        (
          await api.get(
            `/statistiques/clients-vip`
          )
        ).data
      );
    } catch {}

    try {
      setClientsRisque(
        (
          await api.get(
            `/statistiques/clients-risque`
          )
        ).data
      );
    } catch {}

    try {
      setAlertes(
        (
          await api.get(
            `/statistiques/alertes`
          )
        ).data
      );
    } catch {}
  };

  // =====================
  // LOADING
  // =====================

  if (!global) {

    return (
      <div className="p-4 md:p-6">
        Chargement...
      </div>
    );

  }

  const labels = {
    jour: "Jour",
    semaine: "Semaine",
    mois: "Mois",
    annee: "Année"
  };

  // =====================
  // RENDER
  // =====================

  return (
    <div className="p-3 md:p-6 space-y-4 md:space-y-6 bg-gray-100 min-h-[100dvh]">

      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">

        <h2 className="text-2xl md:text-3xl font-extrabold">
          📊 Analyse statistique avancée
        </h2>

        <div className="flex flex-wrap gap-2">

          {[
            "jour",
            "semaine",
            "mois",
            "annee"
          ].map((p) => (

            <button
              key={p}
              onClick={() =>
                setPeriode(p)
              }
              className={`px-3 py-2 rounded-xl text-sm transition ${
                periode === p
                  ? "bg-blue-600 text-white"
                  : "bg-white hover:bg-gray-100"
              }`}
            >
              {labels[p]}
            </button>

          ))}

        </div>

      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 md:gap-4">

        <KPI
          title="Revenus"
          value={`${global.total_revenus} $`}
          color="text-green-600"
        />

        <KPI
          title="Dépenses"
          value={`${global.total_depenses} $`}
          color="text-red-600"
        />

        <KPI
          title="Bénéfice"
          value={`${global.benefice} $`}
        />

        <KPI
          title="Score"
          value={`${global.score}/100`}
          color="text-blue-600"
        />

      </div>

      {/* INSIGHTS */}
      <div className="bg-white p-3 md:p-4 rounded-2xl shadow">

        <h3 className="font-semibold mb-3 text-base md:text-lg">
          🧠 Insights
        </h3>

        <div className="space-y-2 text-sm md:text-base">

          {global.variation < 0 && (
            <p className="text-red-600">
              📉 Revenus en baisse
            </p>
          )}

          {global.variation > 0 && (
            <p className="text-green-600">
              📈 Croissance positive
            </p>
          )}

          {global.score > 80 && (
            <p className="text-blue-600">
              🔥 Excellent score
            </p>
          )}

        </div>

      </div>

      {/* CHARTS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">

        {/* REVENUS */}
        <div className="bg-white p-3 md:p-4 rounded-2xl shadow">

          <h3 className="font-semibold mb-3 text-base md:text-lg">
            📈 Revenus
          </h3>

          <ResponsiveContainer
            width="100%"
            height={260}
          >

            <BarChart
              data={revenusMensuels}
            >

              <CartesianGrid strokeDasharray="3 3" />

              <XAxis dataKey="mois" />

              <YAxis />

              <Tooltip />

              <Bar dataKey="total" />

            </BarChart>

          </ResponsiveContainer>

        </div>

        {/* CROISSANCE */}
        <div className="bg-white p-3 md:p-4 rounded-2xl shadow">

          <h3 className="font-semibold mb-3 text-base md:text-lg">
            📊 Croissance
          </h3>

          <ResponsiveContainer
            width="100%"
            height={260}
          >

            <LineChart
              data={croissance}
            >

              <CartesianGrid strokeDasharray="3 3" />

              <XAxis dataKey="mois" />

              <YAxis />

              <Tooltip />

              <Line dataKey="croissance" />

            </LineChart>

          </ResponsiveContainer>

        </div>

      </div>

      {/* CLIENTS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">

        {/* TOP CLIENTS */}
        <div className="bg-white p-3 md:p-4 rounded-2xl shadow">

          <h3 className="font-semibold mb-3 text-base md:text-lg">
            👤 Top clients
          </h3>

          <div className="space-y-2">

            {topClients.map(
              (c, i) => (

                <div
                  key={i}
                  className="flex justify-between items-center p-2 bg-gray-50 rounded text-sm"
                >

                  <span className="break-words">
                    #{i + 1} - {c.nom}
                  </span>

                  <span className="font-bold text-blue-600 whitespace-nowrap">
                    {c.total} $
                  </span>

                </div>

              )
            )}

          </div>

        </div>

        {/* VIP */}
        <div className="bg-white p-3 md:p-4 rounded-2xl shadow border-t-4 border-green-500">

          <h3 className="font-semibold mb-3 text-base md:text-lg">
            🔥 Clients VIP
          </h3>

          <div className="space-y-2">

            {clientsVIP.map(
              (c, i) => (

                <div
                  key={i}
                  className="p-3 rounded bg-green-50"
                >

                  <div className="font-medium break-words">
                    {c.nom}
                  </div>

                  <div className="text-sm text-gray-600">
                    {c.nb_sejours} séjours
                  </div>

                  <div className="text-green-600 font-bold">
                    {c.total} $
                  </div>

                </div>

              )
            )}

          </div>

        </div>

        {/* RISQUE */}
        <div className="bg-white p-3 md:p-4 rounded-2xl shadow border-t-4 border-red-500">

          <h3 className="font-semibold mb-3 text-base md:text-lg">
            ⚠️ Clients à risque
          </h3>

          <div className="space-y-2">

            {clientsRisque.map(
              (c, i) => (

                <div
                  key={i}
                  className="p-3 rounded bg-red-50"
                >

                  <div className="font-medium break-words">
                    {c.nom}
                  </div>

                  <div className="text-sm text-gray-600">
                    Dernière visite
                  </div>

                  <div className="text-red-600 font-bold">
                    {c.derniere_visite}
                  </div>

                </div>

              )
            )}

          </div>

        </div>

      </div>

      {/* ALERTES */}
      <div className="bg-white p-3 md:p-4 rounded-2xl shadow">

        <h3 className="font-semibold mb-3 text-base md:text-lg">
          🚨 Alertes
        </h3>

        {alertes.length === 0 ? (

          <p className="text-green-600">
            Aucune alerte
          </p>

        ) : (

          <>
            {(showAllAlertes
              ? alertes
              : alertes.slice(0, 5)
            ).map((a, i) => (

              <div
                key={i}
                className="flex items-start p-3 mb-2 rounded-lg bg-red-50 border-l-4 border-red-500"
              >

                <span className="text-sm text-red-700 break-words">
                  ⚠️ {a.message}
                </span>

              </div>

            ))}

            {alertes.length > 5 && (

              <button
                onClick={() =>
                  setShowAllAlertes(
                    !showAllAlertes
                  )
                }
                className="text-blue-600 text-sm mt-2 hover:underline"
              >
                {showAllAlertes
                  ? "Voir moins"
                  : "Voir plus"}
              </button>

            )}

          </>

        )}

      </div>

    </div>
  );
}

function KPI({
  title,
  value,
  color = ""
}) {

  return (
    <div className="bg-white p-3 md:p-4 rounded-2xl shadow">

      <p className="text-sm text-gray-500">
        {title}
      </p>

      <h2
        className={`text-lg md:text-xl font-bold mt-1 break-words ${color}`}
      >
        {value}
      </h2>

    </div>
  );
}

export default Statistiques;